import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import { updateHomeProfileDetails } from "../../../HttpRequest/afterlogin";
import defaultImage from "../../../../assets/img/profiles/icons8-test-account-90.png";

const SuperAdminProfileView = ({ profileData, userProfileUrl, onUpdateProfile, onPhotoUpload }) => {
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [superAdminData, setSuperAdminData] = useState({
    id: profileData?.id || "",
    name: (profileData?.firstName || profileData?.lastName) ? `${profileData?.firstName || ''} ${profileData?.lastName || ''}`.trim() : (profileData?.email || ""),
    email: profileData?.email || "",
    phone: profileData?.phoneNumber || profileData?.mobileNumber || "",
    superAdminId: profileData?.id ? `SA-${profileData.id}` : "",
    roleTitle: profileData?.roleTitle || "Global Platform Administrator & System Lead",
    organization: profileData?.organizationName || profileData?.organization || "",
    systemTier: "Tier 1 Root Governance",
    accountCreated: profileData?.createdDate ? String(profileData.createdDate).substring(0, 10) : ""
  });

  useEffect(() => {
    if (profileData) {
      setSuperAdminData({
        id: profileData?.id || "",
        name: (profileData?.firstName || profileData?.lastName) ? `${profileData?.firstName || ''} ${profileData?.lastName || ''}`.trim() : (profileData?.email || ""),
        email: profileData?.email || "",
        phone: profileData?.phoneNumber || profileData?.mobileNumber || "",
        superAdminId: profileData?.id ? `SA-${profileData.id}` : "",
        roleTitle: profileData?.roleTitle || "Global Platform Administrator & System Lead",
        organization: profileData?.organizationName || profileData?.organization || "",
        systemTier: "Tier 1 Root Governance",
        accountCreated: profileData?.createdDate ? String(profileData.createdDate).substring(0, 10) : ""
      });
    }
  }, [profileData]);

  const val = (v) => (v && String(v).trim() !== "" ? v : "-");

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (superAdminData.id) {
        await updateHomeProfileDetails({
          id: superAdminData.id,
          firstName: superAdminData.name.split(" ")[0] || superAdminData.name,
          lastName: superAdminData.name.split(" ").slice(1).join(" ") || "",
          email: superAdminData.email,
          phoneNumber: superAdminData.phone
        });
      }
      setIsEditing(false);
      if (onUpdateProfile) {
        onUpdateProfile(superAdminData);
      }
      Swal.fire({
        icon: "success",
        title: "Super Admin Profile Updated",
        text: "System administrator settings updated successfully.",
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error("Error updating super admin profile:", error);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: "Could not update super admin details."
      });
    } finally {
      setSaving(false);
    }
  };

  const systemModules = [
    { module: "Tenant & Platform Management", access: "Full Control", desc: "Create, suspend, or configure multi-tenant organizations & portals." },
    { module: "User & Role RBAC Governance", access: "Full Control", desc: "Define global roles, assign granular permissions, and override access controls." },
    { module: "API Gateway & Webhook Controls", access: "Full Control", desc: "Manage REST API keys, rate limits, external Webhook triggers, and integrations." },
    { module: "System Security & Audit Logs", access: "Full Control", desc: "Inspect real-time security alerts, failed authentication attempts, and system audit logs." },
    { module: "Database & S3 Infrastructure", access: "Full Control", desc: "Direct access to S3 document bucket configurations, database backups, and storage quotas." },
    { module: "Financial & Billing Gateways", access: "Full Control", desc: "Manage payment gateways, discount deal configurations, and billing logs." }
  ];

  return (
    <div className="superadmin-profile-container">
      {/* Super Admin Hero Banner */}
      <div className="superadmin-header-card">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="d-flex align-items-center gap-4 flex-wrap">
            <div
              className="profile-avatar-wrapper"
              style={{ borderColor: "#D4AF37", cursor: "pointer" }}
              onClick={() => fileInputRef.current?.click()}
              title="Click to change profile picture"
            >
              <img
                src={userProfileUrl || defaultImage}
                alt="Super Admin Avatar"
                className="profile-avatar-img-large"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = defaultImage;
                }}
              />
              <div className="avatar-edit-overlay" title="Click to upload profile photo">
                <i className="fas fa-camera"></i>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept="image/png, image/jpeg, image/jpg, image/webp"
                onChange={onPhotoUpload}
              />
            </div>
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <span className="badge-role badge-superadmin">
                  <i className="fas fa-user-cog"></i> Super Admin
                </span>
                <span className="badge-status-active">
                  <i className="fas fa-shield-alt"></i> Root Governance Active
                </span>
              </div>
              <h2 className="m-0 text-white font-weight-bold">{val(superAdminData.name)}</h2>
              <p className="text-light opacity-75 mb-1 mt-1" style={{ fontSize: "0.95rem" }}>
                <i className="fas fa-network-wired me-1"></i> {val(superAdminData.roleTitle)}
              </p>
              <p className="text-light opacity-60 m-0" style={{ fontSize: "0.85rem" }}>
                Super Admin ID: <span className="fw-bold">{val(superAdminData.superAdminId)}</span> {superAdminData.organization ? `• ${superAdminData.organization}` : ""}
              </p>
            </div>
          </div>

          <div className="d-flex flex-column align-items-end gap-2">
            {!isEditing ? (
              <button
                className="btn btn-light btn-sm fw-bold px-3 py-2 rounded-pill shadow-sm"
                onClick={() => setIsEditing(true)}
              >
                <i className="fas fa-sliders-h me-1"></i> Edit Account
              </button>
            ) : (
              <button
                className="btn btn-outline-light btn-sm fw-bold px-3 py-2 rounded-pill"
                onClick={() => setIsEditing(false)}
              >
                <i className="fas fa-times me-1"></i> Cancel
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Super Admin Navigation Tabs */}
      <div className="role-nav-tabs">
        <button
          className={`role-tab-btn ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          <i className="fas fa-server me-2"></i> System Overview
        </button>
        <button
          className={`role-tab-btn ${activeTab === "rbac" ? "active" : ""}`}
          onClick={() => setActiveTab("rbac")}
        >
          <i className="fas fa-key me-2"></i> System Permissions & RBAC
        </button>
      </div>

      {/* Tab Content */}
      {!isEditing ? (
        <div>
          {activeTab === "overview" && (
            <div className="role-card">
              <div className="role-card-header">
                <h4 className="role-card-title">
                  <i className="fas fa-server"></i> System Governance & Account Details
                </h4>
              </div>
              <div className="info-grid mb-4">
                <div className="info-item">
                  <div className="info-label">Super Admin Name</div>
                  <div className="info-value">{val(superAdminData.name)}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Super Admin ID</div>
                  <div className="info-value">{val(superAdminData.superAdminId)}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Primary System Email</div>
                  <div className="info-value">{val(superAdminData.email)}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Security Contact Phone</div>
                  <div className="info-value">{val(superAdminData.phone)}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">System Tier Level</div>
                  <div className="info-value font-weight-bold" style={{ color: "#FFE082" }}>{val(superAdminData.systemTier)}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Organization</div>
                  <div className="info-value">{val(superAdminData.organization)}</div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "rbac" && (
            <div className="role-card">
              <div className="role-card-header">
                <h4 className="role-card-title">
                  <i className="fas fa-layer-group"></i> Global Module Access Matrix
                </h4>
              </div>
              <div className="row g-3">
                {systemModules.map((mod, idx) => (
                  <div key={idx} className="col-md-6">
                    <div className="info-item d-flex flex-column gap-2">
                      <div className="d-flex align-items-center justify-content-between">
                        <h6 className="m-0 font-weight-bold" style={{ color: "#FFE082" }}>
                          <i className="fas fa-shield-alt me-2" style={{ color: "#D4AF37" }}></i>{mod.module}
                        </h6>
                        <span className="badge-role badge-superadmin">
                          {mod.access}
                        </span>
                      </div>
                      <small className="text-muted">{mod.desc}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* EDIT SUPER ADMIN FORM */
        <form onSubmit={handleSave} className="role-card">
          <div className="role-card-header">
            <h4 className="role-card-title">
              <i className="fas fa-user-edit"></i> Edit Super Admin Information
            </h4>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="profile-input-group">
                <label className="profile-label">Super Admin Full Name</label>
                <input
                  type="text"
                  className="profile-input"
                  value={superAdminData.name}
                  onChange={(e) => setSuperAdminData({ ...superAdminData, name: e.target.value })}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="profile-input-group">
                <label className="profile-label">Primary System Email</label>
                <input
                  type="email"
                  className="profile-input"
                  value={superAdminData.email}
                  onChange={(e) => setSuperAdminData({ ...superAdminData, email: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="profile-input-group">
                <label className="profile-label">Security Contact Phone</label>
                <input
                  type="text"
                  className="profile-input"
                  value={superAdminData.phone}
                  onChange={(e) => setSuperAdminData({ ...superAdminData, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="profile-input-group">
                <label className="profile-label">System Role Title</label>
                <input
                  type="text"
                  className="profile-input"
                  value={superAdminData.roleTitle}
                  onChange={(e) => setSuperAdminData({ ...superAdminData, roleTitle: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top border-secondary">
            <button
              type="button"
              className="btn-role-secondary"
              onClick={() => setIsEditing(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn-role-primary" disabled={saving}>
              {saving ? "Saving..." : "Save Super Admin Profile"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default SuperAdminProfileView;
